export type LocalMediaState =
  | "paused"
  | "waiting"
  | "loading"
  | "playing"
  | "ended"
  | "pause-failed"
  | "play-failed"
  | "unavailable";

export interface LocalMediaElement extends EventTarget {
  paused: boolean;
  ended: boolean;
  currentTime: number;
  play(): Promise<void>;
  pause(): void;
}
interface Request {
  id: string;
  media: LocalMediaElement;
  beforePlay: () => Promise<boolean>;
  isCurrent: () => boolean;
  onState: (state: LocalMediaState) => void;
}

/** One browser-local owner. Media events never invoke a domain action. */
export class LocalMediaOwner {
  private revision = 0;
  private active?: Request & { ready: boolean };
  private listeners = new Map<LocalMediaElement, () => void>();
  private releasedGuards = new WeakMap<LocalMediaElement, () => void>();

  stop = () => {
    this.revision++;
    const previous = this.active;
    this.active = undefined;
    if (previous) {
      previous.media.pause();
      previous.onState("paused");
    }
  };

  release(media: LocalMediaElement) {
    if (this.active?.media === media) this.stop();
    media.pause();
    this.listeners.get(media)?.();
    this.listeners.delete(media);
    // A browser may resolve an already-requested play after React removes the
    // element. Keep a weak, one-shot guard without retaining unmounted players.
    const guard = () => media.pause();
    const previous = this.releasedGuards.get(media);
    if (previous) media.removeEventListener("play", previous);
    this.releasedGuards.set(media, guard);
    media.addEventListener("play", guard, { once: true });
  }

  async play(request: Request): Promise<void> {
    this.stop();
    const revision = this.revision;
    const active = { ...request, ready: false };
    this.active = active;
    const releasedGuard = this.releasedGuards.get(request.media);
    if (releasedGuard) request.media.removeEventListener("play", releasedGuard);
    this.releasedGuards.delete(request.media);
    const current = () =>
      this.revision === revision &&
      this.active === active &&
      request.isCurrent();
    // Resource failures commonly emit error, reject play(), then emit pause.
    // Completion/failure stays visible until an explicit new listening action.
    let terminal: LocalMediaState | undefined;
    this.listeners.get(request.media)?.();
    const onPlay = () => {
      if (!current() || !active.ready || terminal) request.media.pause();
      else request.onState("playing");
    };
    const onPause = () => {
      if (current() && !terminal) request.onState("paused");
    };
    const onEnded = () => {
      if (current() && !terminal) {
        terminal = "ended";
        request.onState(terminal);
      }
    };
    const onError = () => {
      if (current()) {
        terminal = "unavailable";
        request.onState(terminal);
      }
    };
    const events = {
      play: onPlay,
      pause: onPause,
      ended: onEnded,
      error: onError,
    };
    for (const [event, listener] of Object.entries(events))
      request.media.addEventListener(event, listener);
    this.listeners.set(request.media, () => {
      for (const [event, listener] of Object.entries(events))
        request.media.removeEventListener(event, listener);
    });
    request.onState("waiting");
    let accepted = false;
    try {
      accepted = await request.beforePlay();
    } catch {
      /* The failed command remains in its normal UI. */
    }
    if (!current()) return;
    if (!accepted) {
      terminal = "pause-failed";
      request.onState(terminal);
      return;
    }
    active.ready = true;
    if (request.media.ended) request.media.currentTime = 0;
    request.onState("loading");
    try {
      await request.media.play();
      // A delayed browser promise must not revive audio after navigation. Do not
      // pause a newer, explicitly requested playback of the same element.
      if (!current() && this.active?.media !== request.media)
        request.media.pause();
    } catch {
      if (current() && !terminal) {
        terminal = "play-failed";
        request.onState(terminal);
      }
    }
  }
}
